"use client"

import { useState, useRef, useMemo, useEffect } from "react"
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
} from "@chakra-ui/react"
import { CheckIcon, ChevronDownIcon, SearchIcon } from "@chakra-ui/icons"
import OutlinedIconBtn from "../customButtons/OutlinedIconBtn"
import colors from "common/theme/foundations/colors"
import "common/assets/css/auto-complete.css"
import Spinner from "../spinner/Spinner"

const SearchSelect = ({
  name,
  options = [],
  value,
  placeholder = "Select an option",
  searchPlaceholder = "Search...",
  noOptionsText = "No options available",
  onSelect,
  onChange,
  disabled = false,
  className,
  showSearch = true,
  showFooter = false,
  footerText = "Add new item",
  onFooterClick,
  maxHeight = "200px",
  renderOption,
  renderTrigger,
  isMulti = false,
  loading = false,
  wrapperStyle = {},
  classNamePrefix,
  isSearchable = true,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const dropdownRef = useRef(null)
  const inputRef = useRef(null)

  // Enhanced selectedOptions calculation to handle different value formats
  const selectedOptions = useMemo(() => {
    if (!value) return isMulti ? [] : null

    if (isMulti) {
      if (Array.isArray(value)) {
        return value
          .map((v) => {
            // Normalize to object with value/label
            if (typeof v === 'object' && v?.value) return v
            return options.find((opt) => opt.value === v)
          })
          .filter(Boolean) 
      }
      return []
    } else {
      // Single-select
      if (Array.isArray(value)) {
        const v = value[0]
        return typeof v === 'object' && v?.value
          ? v
          : options.find((opt) => opt.value === v) || null
      }

      return typeof value === 'object' && value?.value
        ? value
        : options.find((opt) => opt.value === value) || null
    }
  }, [options, value, isMulti])


  const filteredOptions = useMemo(() => {
    if (!searchTerm || !isSearchable) return options
    return options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
  }, [options, searchTerm, isSearchable])

  const handleSearchChange = (e) => setSearchTerm(e.target.value)

  const handleOptionClick = (option) => {
    if (disabled) return

    if (isMulti) {
      const currentValues = Array.isArray(value) ? value : []
      let newValues

      // Check if we're working with objects or simple values
      const isObjectValue =
        currentValues.length > 0 && typeof currentValues[0] === "object" && currentValues[0].hasOwnProperty("value")

      if (isObjectValue) {
        // Working with react-select style objects
        const exists = currentValues.some((v) => v.value === option.value)
        newValues = exists ? currentValues.filter((v) => v.value !== option.value) : [...currentValues, option]
      } else {
        // Working with simple values
        const exists = currentValues.includes(option.value)
        newValues = exists ? currentValues.filter((v) => v !== option.value) : [...currentValues, option.value]
      }

      // Call both onSelect and onChange for compatibility
      if (onSelect) {
        onSelect({ name, value: newValues })
      }
      if (onChange) {
        onChange(
          isObjectValue
            ? newValues
            : newValues.map((v) => (typeof v === "object" ? v : options.find((opt) => opt.value === v))),
        )
      }

      // Don't close the dropdown in multi-select mode
      // Just clear the search term
      setSearchTerm("")
    } else {
      // Single select
      if (onSelect) {
        onSelect(name ? { ...option, name } : option)
      }
      if (onChange) {
        onChange(option)
      }
      setIsOpen(false)
      setSearchTerm("")
    }
  }

  const handleRemoveSelected = (valToRemove) => {
    if (!isMulti || !Array.isArray(value)) return

    const isObjectValue = value.length > 0 && typeof value[0] === "object" && value[0].hasOwnProperty("value")
    let newValues

    if (isObjectValue) {
      newValues = value.filter((v) => v.value !== valToRemove)
    } else {
      newValues = value.filter((v) => v !== valToRemove)
    }

    if (onSelect) {
      onSelect({ name, value: newValues })
    }
    if (onChange) {
      onChange(
        isObjectValue
          ? newValues
          : newValues.map((v) => (typeof v === "object" ? v : options.find((opt) => opt.value === v))),
      )
    }
  }

  const toggleDropdown = () => {
    if (disabled) return
    setIsOpen(!isOpen)
    if (!isOpen && showSearch && isSearchable) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
        setSearchTerm("")
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false)
        setSearchTerm("")
      }
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [isOpen])

  const getDisplayOptions = () => {
    if (isMulti) {
      return Array.isArray(selectedOptions) ? selectedOptions : []
    }
    return selectedOptions
  }

  const defaultTriggerRender = () => (
    <Box onClick={toggleDropdown}>
      {isMulti ? (
        <Box
          className={`multiSelectTrigger ${className || ""}`}
          border="1px solid var(--chakra-colors-chakra-border-color)"
          p={2}
          borderRadius="md"
          cursor="pointer"
          minHeight="38px"
          bg={disabled ? "gray.100" : "white"}
        >
          {getDisplayOptions()?.length > 0 ? (
            <Wrap spacing={2}>
              {getDisplayOptions().map((opt) => (
                <WrapItem key={opt.value}>
                  <Tag size="md" borderRadius="full" variant="solid" bg={colors.chipColor}>
                    <TagLabel color={colors.heading}>{opt.label}</TagLabel>
                    <TagCloseButton
                      color={colors.heading}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRemoveSelected(opt.value)
                      }}
                    />
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          ) : (
            <Box color="gray.400">{placeholder}</Box>
          )}
        </Box>
      ) : (
        <OutlinedIconBtn
          variant="outline"
          justifyContent="space-between"
          borderColor="var(--chakra-colors-chakra-border-color)"
          role="combobox"
          width="100%"
          aria-expanded={isOpen}
          color="var(--chakra-colors-gray-800)"
          disabled={disabled}
          title={selectedOptions?.label || placeholder}
          onClick={toggleDropdown}
          rightIcon={<ChevronDownIcon fontSize="xl" color="var(--chakra-colors-placeholder)" />}
          className={className}
        />
      )}
    </Box>
  )

  const defaultOptionRender = (option) => {
    // Determine if this option is selected
    const isSelected = isMulti
      ? Array.isArray(selectedOptions) && selectedOptions.some((selected) => selected.value === option.value)
      : selectedOptions?.value === option.value

    return (
      <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
        <Box>{option.label}</Box>
        {isSelected && (
          <Box fontSize="sm" color="green.500">
            <CheckIcon />
          </Box>
        )}
      </Box>
    )
  }

  return (
    <div className={`searchSelectContainer ${className || ""}`} style={wrapperStyle} ref={dropdownRef}>
      {renderTrigger ? renderTrigger(selectedOptions, placeholder) : defaultTriggerRender()}

      {isOpen && (
        <div className="listContainer">
          {showSearch && isSearchable && (
            <Box className="autoComplete-inputContainer">
              <InputGroup>
                <InputLeftElement>
                  <SearchIcon color={colors.iconDark} />
                </InputLeftElement>
                <Input
                  ref={inputRef}
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="autoComplete-search"
                />
              </InputGroup>
            </Box>
          )}

          <List spacing={1} pb={4} maxHeight={maxHeight} overflow="auto">
            {loading ? (
              <Box py={4}>
                <Spinner size="sm" color="gray.500" />
              </Box>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const isSelected = isMulti
                  ? Array.isArray(selectedOptions) &&
                  selectedOptions.some((selected) => selected.value === option.value)
                  : selectedOptions?.value === option.value

                return (
                  <ListItem
                    key={`${option.value}-${index}`}
                    px="5"
                    py="1"
                    cursor="pointer"
                    bg={isSelected ? "blue.50" : "transparent"}
                    _hover={{ bg: isSelected ? "blue.100" : "gray.200" }}
                    onClick={() => handleOptionClick(option)}
                  >
                    {renderOption ? renderOption(option) : defaultOptionRender(option)}
                  </ListItem>
                )
              })
            ) : (
              <ListItem px="5" py="1" color={colors.text["200"]}>
                {noOptionsText}
              </ListItem>
            )}
          </List>
        </div>
      )}
    </div>
  )
}

export default SearchSelect
